import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { normalizeInlineRADE } from '@rsos/utils/normalizeRADE';
import fetchEmergencySession from '../../../actions/fetchEmergencySession';
import type { RootState } from '../../../app/rootReducer';
import type { RadeCardItem } from './radeSlice';

interface FetchDataLinkArgs {
  mafid: string;
  schema?: { attributes?: Record<string, unknown> };
}

type ThunkAwareDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

/**
 * Provides the `data-link` callback `EllingtonCard` uses to fetch
 * follow-up datasets from inside an already-rendered RAD-e card
 * (e.g. clicking a maf-id row inside a "candidates" card to drill
 * into a single profile).
 *
 * Returns a stable reference suitable for passing through `React.memo`
 * boundaries (`RADECard` is memoized).
 */
export function useFetchRadeDataLink() {
  // Local thunk-aware cast keeps the leaky `as unknown as` out of
  // the call sites; `fetchEmergencySession` is still a `.js` thunk
  // and `useAppDispatch().withTypes()` doesn't carry a thunk
  // signature, so a single boundary cast is the cleanest option.
  const dispatch = useDispatch() as unknown as ThunkAwareDispatch;

  return useCallback(
    async ({
      mafid,
      schema = {},
    }: FetchDataLinkArgs): Promise<RadeCardItem[]> => {
      const { attributes = {} } = schema;
      const { payload } = await dispatch(
        fetchEmergencySession(mafid, { attributes, silentUpdate: true }),
      );
      return normalizeInlineRADE(payload);
    },
    [dispatch],
  );
}

export default useFetchRadeDataLink;
